/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
  EventEmitter,
  Output, AfterViewInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  NgForm,
  NgModel,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { XdaStringsService } from '../../services/xda-strings.service';

@Component({
  selector: 'xrx-textarea',
  templateUrl: './xrx-textarea.component.html',
  styleUrls: ['./xrx-textarea.component.less'],
  standalone: false,
})
export class XrxTextareaComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  @ViewChild('textarea', { static: false }) textarea: ElementRef;

  @Input() label: string;
  @Input() placeholder: string;
  @Input() id: string;
  @Input() name: string;
  @Input() cols: number;
  @Input() rows: number;
  @Input() maxlength: number;
  @Input() maxlengtherror: string;
  @Input() disabled: boolean;
  @Input() readonly: boolean;
  @Input() required: boolean;
  @Input() autofocus: boolean;
  @Input() wrap: string;
  @Input() error: string;
  @Input() ishalf: boolean;
  @Input() isMfd: boolean;
  @Input() classes: string;

  @Output() isValid = new EventEmitter<boolean>();
  @Output() focus = new EventEmitter<any>();
  @Output() blur = new EventEmitter<any>();

  onSubmitError = false;
  timeoutId: any = null;

  constructor(
    public ngControl: NgControl,
    private xdaStringsService: XdaStringsService,
    private form: NgForm,
    private renderer: Renderer2,
  ) {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    const control = this.ngControl.control;
    const validators: ValidatorFn[] = control?.validator
      ? [control.validator]
      : [];
    if (this.required) {
      validators.push(Validators.required);
    }

    control?.valueChanges.subscribe(() => this.valueChanges());
    control?.setValidators(validators);
    control?.updateValueAndValidity();

    this.setupFields();
  }

  ngAfterViewInit() {
    if (this.classes) {
      const arr = this.classes.split(' ');
      arr.forEach((c) => {
        this.renderer.addClass(this.textarea.nativeElement, c);
      });
    }

    if (this.autofocus) {
      this.setFocus();
    }
    const ngModel = this.ngControl as NgModel;
    if (ngModel.viewModel) {
      setTimeout(() => {
        const control = this.ngControl.control;
        control?.markAsDirty();
        control?.markAsTouched();
        control?.updateValueAndValidity();
      }, 1);
    }
  }

  setupFields() {
    if (this.placeholder) {
      const text = this.xdaStringsService.strings[this.placeholder];
      if (text) {
        this.placeholder = text;
      }
    } else {
      this.placeholder = '';
    }
  }

  valueChanges() {
    this.clearCustomError();
    this.isValid.emit(!this.ngControl.control?.invalid);
  }

  clearCustomError() {
    this.error = '';
    this.onSubmitError = false;
  }

  writeValue(obj: any): void {
    // setTimeout because somewhere during initialization the default value is being erased
    setTimeout(() => {
      if (obj !== undefined) {
        this.textarea.nativeElement.value = obj;
        // if entering text greater than max length trim and show max length error
        if (
          this.maxlength > 0 &&
          this.textarea.nativeElement.value.length > this.maxlength
        ) {
          this.ngControl.control?.setValue(
            this.textarea.nativeElement.value.substring(0, this.maxlength),
          );
          this.showMaxLengthError();
        }
      }
    });
  }

  showMaxLengthError() {
    this.ngControl.control?.setErrors({ maxlength: true });
    this.ngControl.control?.markAsDirty();
    this.ngControl.control?.markAsTouched();
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      if (this.ngControl.control?.hasError('maxlength')) {
        if (this.ngControl.control.errors) {
          delete this.ngControl.control.errors['maxlength'];
        }
        this.ngControl.control.markAsPristine();
        this.ngControl.control.updateValueAndValidity();
        this.timeoutId = null;
      }
    }, 3000);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onChange(_event: InputEvent) {}

  onTouched() {}

  onKeyPress(event: KeyboardEvent) {
    // Handle if user tries to enter text past max length
    if (
      this.maxlength > 0 &&
      this.textarea.nativeElement.value.length == this.maxlength &&
      !event.ctrlKey
    ) {
      this.showMaxLengthError();
    }
  }

  onFocus(_event: Event) {
    this.focus.emit(_event.target);
  }

  onBlur(_event: Event) {
    this.blur.emit(_event.target);
  }

  setFocus() {
    this.textarea.nativeElement.focus();
  }

  isErrorState(): boolean {
    return (
      (this.ngControl.control?.invalid &&
        this.ngControl.control?.dirty &&
        !this.ngControl.control?.untouched) ??
      false
    );
  }

  hasAnyError(): boolean {
    return (
      this.ngControl.control?.errors?.['required'] ||
      this.ngControl.control?.errors?.['maxlength'] ||
      this.ngControl.control?.errors?.['customerror']
    );
  }

  onSubmit($event: Event) {
    if (this.form.submitted && this.ngControl.control?.invalid) {
      this.onSubmitError = true;
    }
  }
}
